"use client"

import React, { ReactNode } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"

import { formatPrice } from "@/lib/helper"
import { generateProductLink } from "@/lib/link-utils/link-utils"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import { determinePrefixedProductId } from "@/lib/storefront/product/productHelper"
import { cn } from "@/lib/utils"
import AccountProductTrackingLink from "@/components/account/AccountProductTrackingLink"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"

export default function AccountOrderDetailsLineItemCardClient(props: {
  item: OrderLineItemData
  quantityElement?: ReactNode
  hideSumPrice?: boolean
  isoLocale: string
  sbLocale: string
  trackingList?: string
  searchTerm?: string
}) {
  const t = useTranslations("storefront")

  // contains sbLocale based prefix
  let variantLink: string
  if (props.item.product) {
    variantLink = generateProductLink(props.item.product?.title, props.item.product?.id, undefined, props.sbLocale)
  } else {
    variantLink = ""
  }

  return (
    <div className="col-span-full grid grid-cols-subgrid items-center gap-2 border-b py-2 text-sm last:border-b-0">
      {props.item.productsLoaded ? (
        <>
          {props.item.product ? (
            <AccountProductTrackingLink
              prefetch={false}
              className={`
                flex w-fit cursor-pointer flex-col justify-between underline
                hover:text-primary
                max-lg:gap-y-2
                lg:col-span-2 lg:grid lg:grid-cols-subgrid
              `}
              href={variantLink}
              product={props.item.product}
              trackingList={props.trackingList ?? "account_orders"}
              searchTerm={props.searchTerm ?? props.trackingList ?? "account_orders"}
            >
              <div className="flex justify-center">
                <ImageWithFallback
                  unoptimized={true}
                  className={"aspect-square w-10 object-contain lg:w-20"}
                  src={props.item.product?.image ?? ""}
                  width={80}
                  height={80}
                  alt={props.item.product?.title ?? ""}
                />
              </div>

              <p className="flex items-center text-xs md:text-sm">
                {determinePrefixedProductId(props.item.product?.id ? (props.item.orderLineItem?.product ?? "") : "")}
              </p>
            </AccountProductTrackingLink>
          ) : (
            <>
              {" "}
              {/*no product -> No Link*/}
              <div
                className={`
                  flex w-fit flex-col justify-between underline
                  hover:text-primary
                  max-lg:gap-y-2
                  lg:col-span-2 lg:grid lg:grid-cols-subgrid
                `}
              >
                <div className="flex justify-center">
                  <ImageWithFallback
                    unoptimized={true}
                    className={"aspect-square w-10 object-contain lg:w-20"}
                    src={""}
                    width={80}
                    height={80}
                    alt={""}
                  />
                </div>

                <p className="flex cursor-default items-center text-xs md:text-sm">
                  {determinePrefixedProductId(props.item.orderLineItem?.product ?? "")}
                </p>
              </div>
            </>
          )}

          {props.item.product ? (
            <>
              <div>
                <p className={"text-xs hyphens-auto max-lg:line-clamp-5 md:text-sm lg:max-w-[68ch]"}>
                  {props.item.product?.title ?? props.item.orderLineItem.displayName}
                </p>
              </div>

              <div className="flex flex-col items-end justify-start gap-0.5">
                <span className={"text-xs"}>&nbsp;</span>
                <span>{props.quantityElement ?? props.item.orderLineItem.quantity.value + " x"}</span>
                <span className={"text-xs"}>&nbsp;</span>
              </div>
              <div className={cn({ "text-right": true, "max-lg:hidden": !props.hideSumPrice })}>
                {(() => {
                  try {
                    const discounted = props.item.orderLineItem?.pricing?.singleBasePrice?.net
                    const undiscounted = props.item.orderLineItem?.pricing?.undiscountedSingleBasePrice?.net
                    const dv = Number(discounted?.value ?? 0)
                    const uv = Number(undiscounted?.value ?? 0)
                    const currency = (discounted?.currency as any) ?? (undiscounted?.currency as any)
                    const epsilon = 1e-6
                    const showUndiscounted = uv > 0 && dv + epsilon < uv
                    return showUndiscounted ? (
                      <div className="flex flex-col items-end justify-start gap-0.5">
                        <span className="text-xs text-gray-400 line-through">
                          {formatPrice(props.isoLocale, uv, (undiscounted?.currency as any) ?? currency)}
                        </span>
                        <span>{formatPrice(props.isoLocale, dv, currency)}</span>

                        <span className={"text-xs"}>&nbsp;</span>
                        <span></span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-end justify-start gap-0.5">
                        <span className={"text-xs"}>&nbsp;</span>

                        <span>{formatPrice(props.isoLocale, discounted?.value ?? uv, currency)}</span>
                        <span className={"text-xs"}>&nbsp;</span>
                      </div>
                    )
                  } catch {
                    return formatPrice(
                      props.isoLocale,
                      props.item.orderLineItem?.pricing?.singleBasePrice?.net?.value,
                      props.item.orderLineItem?.pricing?.singleBasePrice?.net?.currency
                    )
                  }
                })()}
              </div>
              {!props.hideSumPrice && (
                <div className="flex flex-col justify-start gap-0.5 text-right">
                  <span>
                    {(() => {
                      try {
                        const discounted = props.item.orderLineItem?.pricing?.price?.net
                        const undiscounted = props.item.orderLineItem?.pricing?.undiscountedPrice?.net
                        const dv = Number(discounted?.value ?? 0)
                        const uv = Number(undiscounted?.value ?? 0)
                        const currency = (discounted?.currency as any) ?? (undiscounted?.currency as any)
                        const epsilon = 1e-6
                        const showUndiscounted = uv > 0 && dv + epsilon < uv
                        return showUndiscounted ? (
                          <div className="flex flex-col items-end justify-start gap-0.5">
                            <span className="text-xs text-gray-400 line-through">
                              {formatPrice(props.isoLocale, uv, (undiscounted?.currency as any) ?? currency)}
                            </span>
                            <span>{formatPrice(props.isoLocale, dv, currency)}</span>

                            <span className={"text-xs text-gray-400"}>{props.item.basePriceInfo}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-end justify-start gap-0.5">
                            <span className={"text-xs"}>&nbsp;</span>
                            <span> {formatPrice(props.isoLocale, discounted?.value ?? uv, currency)}</span>
                            <span className={"text-xs text-gray-400"}>{props.item.basePriceInfo}</span>
                          </div>
                        )
                      } catch {
                        return formatPrice(
                          props.isoLocale,
                          props.item.orderLineItem?.pricing?.price?.net?.value,
                          props.item.orderLineItem?.pricing?.price?.net?.currency
                        )
                      }
                    })()}
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <p className={"text-xs hyphens-auto max-lg:line-clamp-5 md:text-sm lg:max-w-[68ch]"}>
                  {props.item.orderLineItem.displayName ?? ""}
                </p>
                <p className={"text-red-500 italic"}>{t("account.common.product.notfound")}</p>
              </div>
              <div>{props.quantityElement ?? props.item.orderLineItem.quantity.value + " x"}</div>
              <div className={cn({ "text-right": true, "max-lg:hidden": !props.hideSumPrice })}>{"-"}</div>
              <div className={cn({ "text-right": true, "max-lg:hidden": !props.hideSumPrice })}>{"-"}</div>
            </>
          )}
        </>
      ) : (
        <>
          {/*Loading Skeleton*/}
          <div
            className={`
              flex w-fit cursor-pointer flex-col justify-between underline
              hover:text-primary
              max-lg:gap-y-2
              lg:col-span-2 lg:grid lg:grid-cols-subgrid
            `}
          >
            <div className="flex justify-center">
              <div className={"aspect-square w-10 animate-pulse bg-gray-200 object-contain lg:w-20"} />
            </div>
            <div className="flex items-center">
              <p className="h-4 w-16 animate-pulse bg-gray-200 text-xs md:text-sm"> </p>
            </div>
          </div>
          <div>
            <p className="flex h-4 animate-pulse items-center bg-gray-200 text-xs md:text-sm"> </p>
          </div>

          <div className={"h-4 w-16 animate-pulse bg-gray-200"}> </div>
          <div className={"h-4 w-16 animate-pulse bg-gray-200"}> </div>

          <div className="h-4 w-16 animate-pulse bg-gray-200"> </div>
        </>
      )}
    </div>
  )
}
